import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Save, Trash2, Eye, Code2, ExternalLink, Download } from 'lucide-react';
import { api, API, errMsg } from '../../lib/api';
import { TEMPLATE_PARTS } from '../../lib/u99';
import PaperBody from '../paper/PaperBody';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-precision text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</span>
    {children}
  </label>
);
const TEXT_PARTS = TEMPLATE_PARTS.filter((p) => p.key !== 'open_questions');
const inputCls = 'mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-2.5 text-sm text-slate-100 outline-none focus:border-amber-400/60 transition-colors';

export default function DoctrineEditor({ doc, onSaved, onDeleted }) {
  const [form, setForm] = useState({ ...doc, tags: (doc.tags || []).join(', '), open_questions: (doc.open_questions || []).join('\n') });
  const [preview, setPreview] = useState(false);
  const [showTemplate, setShowTemplate] = useState(true);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (overrides = {}) => {
    setBusy(true);
    try {
      const payload = {
        title: form.title, subtitle: form.subtitle, abstract: form.abstract, body_md: form.body_md,
        status: form.status, source_url: form.source_url, slug: form.slug, number: Number(form.number), label: form.label,
        core_statement: form.core_statement, plain_explanation: form.plain_explanation, mechanism: form.mechanism,
        practical_meaning: form.practical_meaning, evidence_status: form.evidence_status,
        open_questions: form.open_questions.split('\n').map((q) => q.trim()).filter(Boolean),
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean), ...overrides,
      };
      const r = await api.put(`/admin/doctrines/${doc.id}`, payload);
      setForm({ ...r.data, tags: (r.data.tags || []).join(', '), open_questions: (r.data.open_questions || []).join('\n') });
      onSaved(r.data);
      toast.success(overrides.status ? `Status → ${overrides.status}` : 'Saved');
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/doctrines/${doc.id}`);
      toast.success('Deleted');
      onDeleted();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const download = () => {
    const blob = new Blob([`# ${form.title}\n\n${form.body_md}`], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${form.slug}.md`;
    a.click();
  };

  const wc = (form.body_md.match(/\w+/g) || []).length;

  return (
    <div className="space-y-6" data-testid="doctrine-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`font-mono-precision text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full border ${form.status === 'published' ? 'border-emerald-400/40 text-emerald-400' : form.status === 'incoming' ? 'border-amber-400/40 text-amber-300' : 'border-white/10 text-slate-500'}`} data-testid="editor-status-badge">{form.status}</span>
          <span className="font-mono-precision text-[10px] tracking-[0.2em] text-slate-600">{wc.toLocaleString()} words</span>
          {form.status !== 'draft' && (
            <Link to={`/u99/doctrines/${form.slug}`} target="_blank" className="flex items-center gap-1.5 font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-cyan-300" data-testid="editor-view-live-link"><ExternalLink size={12} /> View live</Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={download} className="p-2 rounded-full border border-white/10 text-slate-400 hover:text-slate-200" title="Download .md" data-testid="editor-download-btn"><Download size={14} /></button>
          <button onClick={() => setPreview(!preview)} className={`flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition-colors ${preview ? 'border-cyan-400/50 text-cyan-300' : 'border-white/10 text-slate-400 hover:text-slate-200'}`} data-testid="editor-preview-toggle">
            {preview ? <Code2 size={13} /> : <Eye size={13} />} {preview ? 'Markdown' : 'Preview'}
          </button>
          {form.status === 'published' ? (
            <button onClick={() => save({ status: 'draft' })} disabled={busy} className="font-mono-precision text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-full border border-white/10 text-slate-400 hover:text-slate-200" data-testid="editor-unpublish-btn">Unpublish</button>
          ) : (
            <button onClick={() => save({ status: 'published' })} disabled={busy} className="font-mono-precision text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-full border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10" data-testid="editor-publish-btn">Publish</button>
          )}
          <button onClick={() => save()} disabled={busy} className="flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.2em] px-5 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-[#02040a] disabled:opacity-50" data-testid="editor-save-btn"><Save size={13} /> {busy ? 'Saving…' : 'Save'}</button>
          <button onClick={remove} className="p-2 rounded-full border border-white/10 text-slate-500 hover:text-rose-400 hover:border-rose-400/40" data-testid="editor-delete-btn" aria-label="Delete"><Trash2 size={14} /></button>
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_1fr_100px] gap-4">
        <Field label="Title"><input value={form.title} onChange={set('title')} className={inputCls} data-testid="editor-title-input" /></Field>
        <Field label="Subtitle"><input value={form.subtitle} onChange={set('subtitle')} className={inputCls} data-testid="editor-subtitle-input" /></Field>
        <Field label="No."><input type="number" value={form.number} onChange={set('number')} className={inputCls} data-testid="editor-number-input" /></Field>
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        <Field label="Slug"><input value={form.slug} onChange={set('slug')} className={`${inputCls} font-mono-precision text-xs`} data-testid="editor-slug-input" /></Field>
        <Field label="Tags (comma separated)"><input value={form.tags} onChange={set('tags')} className={inputCls} data-testid="editor-tags-input" /></Field>
        <Field label="Label">
          <select value={form.label} onChange={set('label')} className={inputCls} data-testid="editor-label-select">
            <option value="creative-doctrine">Creative doctrine</option><option value="psychological-guidance">Psychological guidance</option><option value="scientific-hypothesis">Scientific hypothesis</option>
          </select>
        </Field>
        <Field label="Status">
          <select value={form.status} onChange={set('status')} className={inputCls} data-testid="editor-status-select">
            <option value="draft">draft (hidden)</option><option value="incoming">incoming (title only)</option><option value="published">published</option>
          </select>
        </Field>
      </div>
      <Field label="Abstract"><textarea rows={3} value={form.abstract} onChange={set('abstract')} className={`${inputCls} resize-y`} data-testid="editor-abstract-input" /></Field>
      <Field label="Source URL (Google Doc etc.)"><input value={form.source_url} onChange={set('source_url')} className={`${inputCls} font-mono-precision text-xs`} data-testid="editor-source-input" /></Field>

      <div className="rounded-2xl border border-amber-400/20 p-5 sm:p-6" data-testid="editor-template-section">
        <button onClick={() => setShowTemplate(!showTemplate)} className="w-full flex items-center justify-between text-left" data-testid="editor-template-toggle">
          <span className="font-celestial text-sm tracking-[0.2em] text-amber-300">SIX-PART TEMPLATE</span>
          <span className="font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-500">{showTemplate ? 'Collapse' : 'Expand'}</span>
        </button>
        {showTemplate && (
          <div className="mt-6 space-y-5">
            {TEXT_PARTS.map((p) => (
              <Field key={p.key} label={`${p.n} · ${p.title} — ${p.hint}`}>
                <textarea rows={p.key === 'core_statement' ? 2 : 5} value={form[p.key] || ''} onChange={set(p.key)} className={`${inputCls} resize-y`} data-lenis-prevent data-testid={`editor-${p.key}-input`} />
              </Field>
            ))}
            <Field label="06 · Open questions — one per line">
              <textarea rows={4} value={form.open_questions} onChange={set('open_questions')} className={`${inputCls} resize-y`} data-lenis-prevent data-testid="editor-open_questions-input" />
            </Field>
          </div>
        )}
      </div>

      {preview ? (
        <div className="rounded-2xl border border-white/[0.08] p-8 lg:p-12 bg-[#03060e] max-h-[75vh] overflow-y-auto" data-lenis-prevent data-testid="editor-preview">
          <PaperBody markdown={form.body_md || '_Nothing to preview yet._'} />
        </div>
      ) : (
        <Field label={`Body · Markdown · ${API}/admin/doctrines/${doc.id}/markdown`}>
          <textarea value={form.body_md} onChange={set('body_md')} rows={28} spellCheck={false} className={`${inputCls} font-mono-precision text-[13px] leading-relaxed resize-y min-h-[50vh]`} placeholder={'# Heading\n\nParagraphs, **bold**, *italic*, > quotes, - lists…'} data-lenis-prevent data-testid="editor-body-input" />
        </Field>
      )}
    </div>
  );
}
